import * as THREE from 'three';
import { TweenLite, Power0, Sine } from 'gsap';
import Stats from 'stats.js';
import context from './context'

const includeStats = false && window.pw.debug;
export default class Three {

    constructor() {

        this.width = 0;
        this.height = 0;
        this.three = {};
        this.loops = [];
        this.frame = null;
        this.wipeInterval = null;
        this.wipeTween = null;

        if(includeStats) {
            this.stats = new Stats();
        }
        
        this.initThree();
    }

    initThree = () => {
        const clock = new THREE.Clock();        
        const camera = new THREE.PerspectiveCamera( 70, 1, 0.01, 100 );
        const scene = new THREE.Scene();
        const renderer = new THREE.WebGLRenderer({ 
            preserveDrawingBuffer: true,
            antialias: true
        });
        renderer.autoClearColor = false;

        const camwrap = new THREE.Object3D();
        camwrap.add(camera);
        scene.add(camwrap);

        // Make highly-transparent plane
        const fadeMaterial = new THREE.MeshBasicMaterial({
            color: 0x000000,
            transparent: true,
            opacity: 0.5
        });
        const fadePlane = new THREE.PlaneBufferGeometry(1, 1);
        const plane = new THREE.Mesh(fadePlane, fadeMaterial);
        plane.position.z = -0.1;
        plane.renderOrder = -1;

        camwrap.add(plane);
        camwrap.position.z = 32;

        this.three = { renderer, camera, camwrap, plane, scene, clock };

        this.componentDidMount();
    }

	componentDidMount = () => {

        //this.mount.current.appendChild(this.three.renderer.domElement); 
        document.querySelector('.canvas').appendChild(this.three.renderer.domElement);
        
        window.document.addEventListener('gamMapLoaded', this.handleMapLoaded);
        window.document.addEventListener('gamLinkLoading', this.handleLink);

        //window.addEventListener('orientationchange', this.handleResize);
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
        
        if(includeStats) {
            document.body.appendChild( this.stats.dom );
        }

        this.animate();
    }
    
    componentWillUnmount = () => {

        window.document.removeEventListener('gamMapLoaded', this.handleMapLoaded);
        window.document.removeEventListener('gamLinkLoading', this.handleLink);

        //window.removeEventListener('orientationchange', this.handleResize);
        window.removeEventListener('resize', this.handleResize);
        
        if(includeStats) {
            document.body.removeChild( this.stats.dom );
        }

        cancelAnimationFrame(this.frame);
    }

    handleResize = () => {

        //ignore "small" viewport changes through native navbars
        if(context.isTouch && Math.abs(this.height - window.innerHeight) <= 114) return;
        
        let { innerWidth: width, innerHeight: height } = window;

        const aspect = 16/9;
        const aspectMobile = 414/736; //iPhone 6+
        
        if(context.mqSmall.matches) {

            if(width/height >= aspectMobile) {
                //console.log('MOBILE recalc height based on width')
                height = width / aspectMobile;
            } else {
                //console.log('MOBILE recalc width based on height')
                width = height * aspectMobile;
            }


        //for tablet+: keep 16:9 to be able to exactly map the 16:9 intro image
        } else {
            if(width/height >= aspect) {
                //console.log('recalc height based on width')
                height = width / aspect;
            } else {
                //console.log('recalc width based on height')
                width = height * aspect;
            }
        }
        
        

        this.width = width;
        this.height = height;

        const { camera, renderer } = this.three;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();        
        renderer.setSize(width, height, false);
    }

    handleMapLoaded = (e) => {

        //if this is a page transition, let handleLink do all the work
        if(!context.isInitial && e.detail.options.isNewPage) return;
        const defaults = {
            clear: 0.1,
            wipe: [0.66, 1, 30]
        };
        const options = Object.assign(defaults, e.detail.options);
        const { clear, wipe } = options;
        
        const tweenDuration = context.isInitial ? 0 : 4;

        //tween to new plane opacity
        if(this.wipeTween) this.wipeTween.kill();
        this.wipeTween = TweenLite.to(this.three.plane.material, tweenDuration, {
                opacity: clear,
                ease: Sine.easeInOut,
                onComplete: () => {
                    this.setWipe(wipe, clear);
                }
        });
    }

    handleLink = () => {
        
        //prevent wipe on transition
        if(this.wipeInterval) window.clearInterval(this.wipeInterval);

        //unclear screen
        const p1 = new Promise((resolve) => {
            if(this.wipeTween) this.wipeTween.kill();
            this.wipeTween = TweenLite.to(this.three.plane.material, 2, {
                opacity: 0.02,
                ease: Sine.easeInOut,
                onComplete: () => {
                    resolve();
                }
            });
        
        });

        //wait for next map load
        const p2 = new Promise(function(resolve) {

            window.document.addEventListener('gamMapLoaded', mapLoaded);

            function mapLoaded(e) {
                window.document.removeEventListener('gamMapLoaded', mapLoaded);
                resolve(e);
            }
        });

        //if unclear transition is done and new map is loaded,
        //make a clear=1 and then go to target clear
        Promise.all([p1, p2]).then((values) => {
            
            const { clear, wipe } = values[1].detail.options;
                
            //full clear the transition artifacts with a short delay
            if(this.wipeTween) this.wipeTween.kill();
            this.wipeTween = TweenLite.to(this.three.plane.material, 2, {
                opacity: 1,
                ease: Sine.easeIn,
                delay: 9,
                onComplete: () => {
                    //console.log('full clear complete');
                    this.wipeTween = TweenLite.to(this.three.plane.material, 4, {
                        opacity: clear,
                        ease: Sine.easeInOut,
                        onComplete: () => {
                            this.setWipe(wipe, clear);
                        }
                    });
                }
            });

        })
    }

    setWipe(wipe, clear) {

        const opacity = wipe[0], 
            duration = wipe[1], 
            every = wipe[2];
        
        if(this.wipeInterval) window.clearInterval(this.wipeInterval);
        this.wipeInterval = window.setInterval(() => {
            if(this.wipeTween) this.wipeTween.kill();
            this.wipeTween = TweenLite.to(this.three.plane.material, duration/2, {
                opacity: opacity,
                ease: Power0.easeInOut,
                onComplete: () => {
                    //savely reverse to clear color
                    this.wipeTween = TweenLite.to(this.three.plane.material, duration/2, {
                        opacity: clear, ease: Power0.easeInOut
                    });
                }
            });
        }, every * 1000);
    }

    addLoop = (func) => {
        this.loops.push(func);
    }

    removeLoop = (func) => {
        const i = this.loops.indexOf(func);
        if(i > -1) this.loops.splice(i, 1);
    }

    animate = () => {

        if(includeStats) this.stats.begin();

        const {renderer, camera, scene} = this.three;
        for(let loop of this.loops) loop();
        renderer.render( scene, camera );
        this.frame = requestAnimationFrame( this.animate );
        
        if(includeStats) this.stats.end();
    }
}